// authored by Alex Cooksey
import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import Avatar from '../Avatar/Avatar';
import Button from '../Button';
import Wrapper from './Wrapper';
import iconSet from '../../images/teambuildr-selection.json';
import { ColorGreen, ColorWhite } from '../../GlobalStyles';

const AvatarDateContainer = styled('div')`
  align-items: center;
  display: flex;
`;

const CloseButtonContainer = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
`;

const EntryActionsContainer = styled('div')`
  align-items: center;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  display: flex;
  height: 50px;
  justify-content: space-between;
  padding-top: 8px;
  width: 100%;
  z-index: 1;
  @media screen and (max-width: 570px) {
    flex-wrap: wrap;
    height: auto;
    min-height: 50px;
    & > button:last-child {
      width: 100%;
      margin-top: 8px;
    }
  }
`;

const EntryDate = styled('div')`
  margin-left: 16px;
  text-align: left;
  h4 {
    color: ${({ theme }) => theme.colors.lightText[0]};
    font-family: 'Nunito Sans';
    font-feature-settings: 'clig' off, 'liga' off;
    font-size: 16px;
    margin-bottom: 0;
  }
`;

const NavigationWrapper = styled('div')`
  align-items: start;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: none;
  border-top-left-radius: ${(props) => props.borderRadius};
  border-top-right-radius: ${(props) => props.borderRadius};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  font-weight: 500;
  height: 44px;
  justify-content: space-between;
  padding: 0;
  top: 0;
  width: 100%;
  z-index: 1;
`;

export const PrimaryWrapper = styled('div')`
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border-radius: ${(props) => props.borderRadius};
  display: flex;
  flex-direction: column;
  font-family: 'Nunito Sans';
  justify-content: center;
  padding: ${(props) => props.padding};
  width: 100%;
  .text-area {
    background: ${({ theme }) => theme.colors.surfaceElevated};
    border: none;
    box-shadow: none;
    font-size: 28px;
    height: 200px;
    outline: none;
    padding: 20px 0px;
    resize: none;
    .rc-textarea {
      height: 100%;
      padding: 0px;
    }
  }
  @media screen and (max-width: 570px) {
    padding: 20px 10px 10px 10px;
  }
`;

/**
 * @param {String} borderRadius corner radius applied to the modal's outer edges
 * @param {Node} children content rendered inside the modal body
 * @param {String} currentUserName name of the current user used in Avatar
 * @param {String} customPadding custom padding for the primary wrapper
 * @param {String} displayDate formatted date string shown in the modal header
 * @param {Array} gradientColors gradient color pair for the user avatar background
 * @param {Boolean} isEditMode determines if the modal is in edit (UPDATE) or create (POST) mode
 * @param {Function} onClose callback invoked when the modal is closed
 * @param {Function} onSubmit callback invoked when the submit button is clicked
 * @param {Node} postActions additional action elements rendered alongside the submit button
 * @param {Boolean} submitDisabled disables the submit button when true
 * @param {String} userPic URL of the user's avatar image
 */
const CreateEditEntryModal = ({
  borderRadius,
  children,
  currentUserName,
  customPadding,
  displayDate,
  gradientColors,
  isEditMode,
  onClose,
  onSubmit,
  postActions,
  submitDisabled,
  userPic,
}) => {
  const { colors } = useTheme();
  const renderHeader = () => (
    <NavigationWrapper borderRadius={borderRadius}>
      <AvatarDateContainer>
        <Avatar
          avatar={userPic || ''}
          name={currentUserName}
          size={44}
          linearGradient={gradientColors}
          showGradientRing
        />
        <EntryDate>
          <h4>{displayDate}</h4>
        </EntryDate>
      </AvatarDateContainer>
      <CloseButtonContainer onClick={onClose} aria-label='Close modal'>
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='remove'
          color={colors.text}
        />
      </CloseButtonContainer>
    </NavigationWrapper>
  );

  const renderSubmitButton = () => (
    <Button
      bgColor={ColorGreen}
      borderColor={ColorGreen}
      customBorderRadius='8px'
      disabled={submitDisabled}
      name={isEditMode ? 'UPDATE' : 'POST'}
      onClick={onSubmit}
      txtColor={ColorWhite}
      type='submit'
    />
  );

  const renderPostActions = () => (
    <EntryActionsContainer>
      {postActions}
      {renderSubmitButton()}
    </EntryActionsContainer>
  );

  return (
    <Wrapper>
      <PrimaryWrapper padding={customPadding || '20px'} borderRadius={borderRadius}>
        {renderHeader()}
        {children}
        {renderPostActions()}
      </PrimaryWrapper>
    </Wrapper>
  );
};

CreateEditEntryModal.propTypes = {
  borderRadius: PropTypes.string,
  children: PropTypes.node.isRequired,
  currentUserName: PropTypes.string,
  customPadding: PropTypes.string,
  displayDate: PropTypes.string.isRequired,
  gradientColors: PropTypes.arrayOf(PropTypes.string),
  isEditMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  postActions: PropTypes.node.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  userPic: PropTypes.string,
};

CreateEditEntryModal.defaultProps = {
  borderRadius: '12px',
  currentUserName: '',
  customPadding: '20px',
  gradientColors: undefined,
  userPic: '',
};

export default CreateEditEntryModal;
